// Small presentational helpers shared by the backups views.

export function timeAgo(iso) {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diff)) return '—';
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

export function formatTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
}

export const shortSha = (sha) => (sha ? sha.slice(0, 12) : '');

/** What the history calls a commit: netcd's commit id, or "save". */
export function commitLabel(commit) {
    if (commit?.manifest?.commit) return commit.manifest.commit;
    if (/^save\b/.test(commit?.subject || '')) return 'save';
    return shortSha(commit?.sha);
}

const DELIVERY = {
    acknowledged: { label: 'Delivered', icon: 'check_circle', tone: 'ok',
        hint: 'The router confirmed the server holds its latest configuration.' },
    pending: { label: 'Awaiting ack', icon: 'schedule', tone: 'warn',
        hint: 'Received and stored; the router has not answered the acknowledgement yet.' },
    refused: { label: 'Ack refused', icon: 'report', tone: 'warn',
        hint: 'Stored, but the router refused the acknowledgement. Its netcd may need the update that accepts a token-less ack, or its cloud policy is disabled.' },
    failed: { label: 'Apply failed', icon: 'error', tone: 'error',
        hint: 'The last bundle could not be applied. The router will resend.' },
    conflict: { label: 'Conflict', icon: 'warning', tone: 'error',
        hint: "The router sends a history that is not this repository's: it looks replaced. An admin must start a new repository." },
    none: { label: 'No backups', icon: 'remove', tone: 'idle',
        hint: 'Nothing received. On the router: configure system backup enable true, then commit.' },
};

export function DeliveryBadge({ delivery }) {
    const d = DELIVERY[delivery] || DELIVERY.none;
    return (
        <span className={`bk-badge bk-badge-${d.tone}`} title={d.hint}>
            <span className="material-symbols-outlined" aria-hidden="true">{d.icon}</span>
            {d.label}
        </span>
    );
}

export function deliveryHint(delivery) {
    return (DELIVERY[delivery] || DELIVERY.none).hint;
}

export function Loading({ label = 'Loading' }) {
    return (
        <div className="bk-loading" role="status">
            <span className="material-symbols-outlined bk-spin" aria-hidden="true">sync</span>
            <span className="bk-sr-only">{label}</span>
        </div>
    );
}

export function ErrorBanner({ msg, onClose }) {
    if (!msg) return null;
    return (
        <div className="bk-error-banner" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">error</span>
            <span>{msg}</span>
            {onClose && (
                <button type="button" className="bk-icon-btn bk-ml-auto" onClick={onClose} aria-label="Dismiss message">
                    <span className="material-symbols-outlined" aria-hidden="true">close</span>
                </button>
            )}
        </div>
    );
}

export function Empty({ icon, title, children }) {
    return (
        <div className="bk-empty">
            <span className="material-symbols-outlined bk-empty-icon" aria-hidden="true">{icon}</span>
            <div className="bk-empty-title">{title}</div>
            {children && <div className="bk-empty-text">{children}</div>}
        </div>
    );
}
